import React, { useCallback, useState } from 'react';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { CvtLeftAside } from '../components/cvt-left-aside';
import { DetailView, DetailViewContent, DetailViewHeader } from '../../components/detail-view';
import { getDisplayValue } from '@web/utils';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { useCvtDiagnosticReport } from '../hook/use-cvt-diagnostic-report';
import { useNavigate, useParams } from 'react-router-dom';
import colors from '@packages/core/styles/colors';
import CvtReportDetails from '../components/cvt-report-details';
import CvtRightAside from '../components/cvt-right-aside';
import ToastNotification from '@web/components/toast-notification';

interface CvtReportStyleProps {
    hideRightAside: boolean;
}

const useCvtStyles = createUseStyles({
    collapseButton: ({ hideRightAside }: CvtReportStyleProps) => ({
        alignItems: 'center',
        backgroundColor: colors.blueFour,
        borderRadius: '50%',
        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
        cursor: 'pointer',
        display: 'flex',
        height: 24,
        justifyContent: 'center',
        left: hideRightAside ? -28 : 0,
        position: 'absolute',
        width: 24,
    }),
    collapseButtonOuter: {
        bottom: 32,
        position: 'fixed',
        zIndex: 999,
    },
    rightSideWrapper: {
        display: 'flex',
    },
    rightAside: ({ hideRightAside }: CvtReportStyleProps) => ({
        height: '100%',
        overflowY: hideRightAside ? 'hidden' : 'auto',
        position: 'relative',
        transition: 'width 0.2s ease',
        width: hideRightAside ? 0 : 390,
    }),
    inlineMiddle: {
        display: 'inline-block',
        verticalAlign: 'middle',
        marginLeft: 8,
        marginBottom: 0,
    },
    gap: {
        gap: 30,
    },
});

export const CvtReport: React.FC = () => {
    const navigate = useNavigate();
    const { vin: vinString, id } = useParams<{ vin: string; id: string }>();
    const { data, graphData, isLoading, downloadingIds, downloadReport } = useCvtDiagnosticReport(id);
    const [showToast, setShowToast] = useState({ show: false, message: '' });
    const [hideRightAside, setHideRightAside] = useState(false);
    const classes = useCvtStyles({ hideRightAside });

    const isDownloading = !!id && downloadingIds.has(id);

    async function handleDownloadButtonClick(diagnosticId: string | undefined) {
        if (!diagnosticId || downloadingIds.has(diagnosticId)) {
            return;
        }
        const result = await downloadReport(diagnosticId);
        if (result) {
            setShowToast({ show: true, message: 'The CVT diagnostic document has been successfully downloaded.' });
        }
    }

    const handleBackButtonClick = useCallback(() => {
        if (window.history.state && window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(`/vin/${vinString}/cvt`);
        }
    }, [navigate, vinString]);

    return (
        <>
            <DetailView>
                <DetailViewHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className={`${classes.gap} d-flex align-items-center`}>
                            <Button variant="link" onPress={handleBackButtonClick}>
                                <Icon name="arrow-back" />
                            </Button>
                            <Typography variant="h3">
                                Diagnostic ID:{' '}
                                {isLoading && !data?.sbrDiagnosticId ? (
                                    <SkeletonLoader
                                        type="text"
                                        width={160}
                                        height={24}
                                        className={classes.inlineMiddle}
                                    />
                                ) : (
                                    getDisplayValue(data?.diagnosticId)
                                )}
                            </Typography>
                        </div>
                        {!isLoading ? (
                            <Button
                                variant="ghost-blue"
                                isLoading={isDownloading}
                                disabled={isDownloading}
                                onPress={() => handleDownloadButtonClick(id)}
                            >
                                <Icon name="sync-down" color="blueOne" />
                            </Button>
                        ) : (
                            <SkeletonLoader type="text" width={56} height={40} className={classes.inlineMiddle} />
                        )}
                    </div>
                </DetailViewHeader>

                <DetailViewContent>
                    <CvtLeftAside />
                    <CvtReportDetails graphData={graphData} isLoading={isLoading} sbrData={data?.sbrData} />
                    <div className={classes.rightSideWrapper}>
                        <div className={classes.collapseButtonOuter}>
                            <Button variant="link" onPress={() => setHideRightAside((v) => !v)}>
                                <div className={classes.collapseButton}>
                                    <Icon
                                        name={hideRightAside ? 'chevron-left' : 'chevron-right'}
                                        color="blueOne"
                                        size={16}
                                    />
                                </div>
                            </Button>
                        </div>
                        <div className={classes.rightAside}>
                            <CvtRightAside
                                accountName={data?.accountName}
                                jobCompletedTimestamp={data?.jobCompletedTimestamp}
                                retailer={data?.retailer}
                            />
                        </div>
                    </div>
                </DetailViewContent>
            </DetailView>

            <ToastNotification
                show={showToast.show}
                onClose={() => setShowToast({ ...showToast, show: false })}
                body={
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <Icon name="checkmark" size={20} color="white" />
                        <Typography variant="h6" color="white">
                            {showToast.message}
                        </Typography>
                    </div>
                }
                autoHideTime={2000}
                variant="success"
                position="top-center"
            />
        </>
    );
};
